import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";
import {
  playCorrect,
  playWrong,
  playCombo,
  playGolden,
  playTreasure,
  playWin,
  playGameOver,
  playFreeze,
} from "../utils/emojiSounds";

const SPEEDS = {
  slow: 1800,
  medium: 1100,
  fast: 700,
  crazy: 400,
};

const GRID_SIZES = {
  1: 1,
  2: 4,
  3: 9,
  4: 16,
  5: 25,
  6: 36,
  7: 49,
  8: 64,
};

const DEFAULT_SETTINGS = {
  gridSize: 3,
  category: "animals",
  speed: "medium",
  difficulty: "medium",
};

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const random = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export default function useEmojiGame() {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const userId = authState?.user?._id || null;
  const userName = authState?.user?.name || null;
  const userCode = authState?.user?.think5whyscode || null;
  const token = authState.token;

//   const PENALTY = {
//     easy: 5,
//     medium: 10,
//     hard: 25,
//     extreme: 50,
//   }[settings.difficulty] || 10;

  // ─── UI / Screen State ───────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [screen, setScreen] = useState("home"); // "home" | "play" | "result"
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);

  // ─── Settings ────────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const PENALTY = useMemo(() => {
    return {
        easy: 5,
        medium: 10,
        hard: 25,
        extreme: 50,
    }[settings.difficulty] || 10;
  }, [settings.difficulty]);

  // ─── Game Data ───────────────────────────────────────────────────────────
  const [emojiQueue, setEmojiQueue] = useState([]);
  const [visibleEmojis, setVisibleEmojis] = useState([]);
  const [grid, setGrid] = useState([]);
  const [collectedIds, setCollectedIds] = useState([]);

  // ─── Score / Stats ───────────────────────────────────────────────────────
  const [score, setScore] = useState(0);
  const [scoreLedger, setScoreLedger] = useState([]);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  // ─── Special / Bonus ─────────────────────────────────────────────────────
  const [goldenEmojiId, setGoldenEmojiId] = useState(null);
  const [treasureEmojiId, setTreasureEmojiId] = useState(null);
  const [freezeUntil, setFreezeUntil] = useState(0);
  const [frozen, setFrozen] = useState(false);
  const [gameMessage, setGameMessage] = useState("");
  const [targetTime, setTargetTime] = useState(30 + Math.floor(Math.random() * 16)); // 30-45s

  // ─── Backend Data ────────────────────────────────────────────────────────
  const [leaderboard, setLeaderboard] = useState([]);
  const [streakHistory, setStreakHistory] = useState([]);
  const [bestRecords, setBestRecords] = useState([]);

  // ─── Refs ────────────────────────────────────────────────────────────────
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const comboRef = useRef(0);
  const lastTapRef = useRef(0);
  const queueRef = useRef([]);
  const collectedRef = useRef([]);
  const visibleEmojisRef = useRef([]);
  const elapsedRef = useRef(0);

  // Keep refs in sync
  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  useEffect(() => {
    visibleEmojisRef.current = visibleEmojis;
  }, [visibleEmojis]);

  // ─── Derived ─────────────────────────────────────────────────────────────
  const gridCapacity = useMemo(
    () => GRID_SIZES[settings.gridSize] || 9,
    [settings.gridSize]
  );

  const myStats = useMemo(() => {
    const bestScore =
      bestRecords.find(
        (b) =>
          Number(b.gridSize) === Number(settings.gridSize) &&
          b.category === settings.category
      ) || null;
    return { bestScore };
  }, [bestRecords, settings.gridSize, settings.category]);

  // ─── Settings Helpers ────────────────────────────────────────────────────
  const changeGridSize = useCallback((size) => {
    setSettings((prev) => ({ ...prev, gridSize: size }));
  }, []);

  const changeSpeed = useCallback((speed) => {
    setSettings((prev) => ({ ...prev, speed }));
  }, []);

  const setCategory = useCallback((category) => {
    setSettings((prev) => ({ ...prev, category }));
  }, []);

  const updateSettings = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ─── Data Loading ────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!userId || !token) return;
    try {
      const config = {
        url: "/emojigrid/history",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.success) {
        setStreakHistory(response.history || []);
        setBestRecords(response.bestRecords || []);
      }
    } catch (e) {
      console.error("Emoji Hunt History Fetch Error:", e);
      setStreakHistory([]);
    }
  }, [userId, token, fetchData]);

  const fetchLeaderboard = useCallback(async () => {
    if (!token) return { success: false, leaderboard: [] };
    try {
      const config = {
        url: "/emojigrid/leaderboard",
        method: "post",
        headers: { Authorization: token },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.success) {
        setLeaderboard(response.leaderboard || []);
      } else {
        setLeaderboard([]);
      }
      return response;
    } catch (err) {
      console.error(err);
      setLeaderboard([]);
      return { success: false, leaderboard: [] };
    }
  }, [token, fetchData]);

  const submitScore = useCallback(
    async (payload) => {
      try {
        const config = {
          url: "/emojigrid/submit",
          method: "post",
          data: { userId, ...payload },
          headers: { Authorization: token },
        };
        return await fetchData(config, { silent: true });
      } catch (err) {
        console.error("Emoji Hunt Save Score Error:", err);
        return { success: false };
      }
    },
    [userId, token, fetchData]
  );

  const confirmStreakEntry = useCallback(
    async (payload) => {
      try {
        const config = {
          url: "/emojigrid/streaks/confirm",
          method: "post",
          data: { userId, ...payload },
          headers: { Authorization: token },
        };
        return await fetchData(config, { silent: true });
      } catch (err) {
        console.error("Emoji Hunt Confirm Streak Error:", err);
        return { success: false };
      }
    },
    [userId, token, fetchData]
  );

  const loadEmojiBatch = useCallback(async () => {
    try {
      const alreadyInPlay = [
        ...collectedRef.current,
        ...queueRef.current.map((e) => e.id),
        ...visibleEmojisRef.current.map((e) => e.id),
      ];

      const config = {
        url: "/emojigrid/emojis",
        method: "post",
        data: {
          category: settings.category,
          limit: 40,
          exclude: alreadyInPlay,
        },
        headers: { Authorization: token },
      };

      const res = await fetchData(config, { silent: true });
      const list = res?.emojis || [];

      if (list.length) {
        const existingIds = new Set(alreadyInPlay);
        const batch = shuffle(list).filter((e) => !existingIds.has(e.id));

        queueRef.current = [...queueRef.current, ...batch];
        setEmojiQueue([...queueRef.current]);

        setVisibleEmojis((prev) => {
          const copy = [...prev];
          while (copy.length < 18 && queueRef.current.length) {
            copy.push(queueRef.current.shift());
          }
          return copy;
        });
      }
    } catch (err) {
      console.error("Emoji Fetch Error:", err);
    }
  }, [settings.category, token, fetchData]);

  // ─── Game Mechanics ──────────────────────────────────────────────────────
  const refillVisible = useCallback(() => {
    if (queueRef.current.length < 20) loadEmojiBatch();

    setVisibleEmojis((prev) => {
      const list = [...prev];
      while (list.length < 18 && queueRef.current.length) {
        list.push(queueRef.current.shift());
      }
      return list;
    });
  }, [loadEmojiBatch]);

  const calculateProgress = useCallback(
    (filled) => {
      setProgress(Math.min(100, Math.round((filled / gridCapacity) * 100)));
    },
    [gridCapacity]
  );

  const removeVisibleEmoji = useCallback((id) => {
    setVisibleEmojis((prev) => {
      const next = prev.filter((e) => e.id !== id);
      while (next.length < 18 && queueRef.current.length) {
        next.push(queueRef.current.shift());
      }
      return next;
    });
  }, []);

  const pushToGrid = useCallback(
    (emoji) => {
      setGrid((prev) => {
        const next = [...prev, emoji];
        calculateProgress(next.length);
        return next;
      });
    },
    [calculateProgress]
  );

  const onEmojiTap = useCallback(
    (emoji) => {
      if (!playing) return;

      // Duplicate check
      if (collectedRef.current.includes(emoji.id)) {
        setScore((s) => s - PENALTY);
        setScoreLedger((prev) => [
          ...prev,
          {
            type: "duplicate",
            points: -PENALTY,
            reason: `${emoji.char || emoji.emoji || emoji.symbol || "Emoji"} (already collected)`,
            at: elapsedRef.current,
          },
        ]);
        return;
      }

      const now = Date.now();
      const isCorrect =
        settings.category === "mixed" ? true : emoji.category === settings.category;

      if (isCorrect) {
        playCorrect();
      } else {
        playWrong();
      }

      if (!isCorrect) {
        setWrong((prev) => prev + 1);
        comboRef.current = 0;
        setCombo(0);
        setGameMessage("❌ Oops!");
        setScore((prev) => prev - 10);
        setScoreLedger((prev) => [
          ...prev,
          {
            type: "wrong",
            points: -PENALTY,
            reason: `${emoji.char || emoji.emoji || emoji.symbol || "Emoji"} (wrong pick)`,
            at: elapsedRef.current,
          },
        ]);
        return;
      }

      // Correct pick
      collectedRef.current.push(emoji.id);
      setCollectedIds([...collectedRef.current]);
      removeVisibleEmoji(emoji.id);
      pushToGrid(emoji);

      setCorrect((prev) => {
        const newCorrect = prev + 1;
        // Accuracy is also kept in sync via effect, but we can update here too
        return newCorrect;
      });

      // Combo
      if (now - lastTapRef.current < 1200) {
        comboRef.current += 1;
      } else {
        comboRef.current = 1;
      }
      lastTapRef.current = now;
      setCombo(comboRef.current);
      setBestCombo((prev) => Math.max(prev, comboRef.current));

      // Score calculation
      let gained = 100;
      if (comboRef.current >= 2) gained += comboRef.current * 20;
      if (emoji.id === goldenEmojiId) {
        gained += 500;
        playGolden?.();
      }
      if (emoji.id === treasureEmojiId) {
        gained += 1000;
        playTreasure?.();
      }

      setScore((prev) => prev + gained);
      setScoreLedger((prev) => [
        ...prev,
        {
          type: "correct",
          points: gained,
          reason: emoji.char || emoji.emoji || emoji.symbol || "Emoji",
          at: elapsedRef.current,
        },
      ]);
      setGameMessage(`+${gained}`);

      // Bonus spawns
      if (Math.random() < 0.08 && !goldenEmojiId) {
        const available = visibleEmojisRef.current.filter(
          (e) => !collectedRef.current.includes(e.id)
        );
        if (available.length) {
          const lucky = available[random(0, available.length - 1)];
          setGoldenEmojiId(lucky.id);
        }
      }

      if (Math.random() < 0.03 && !treasureEmojiId) {
        const available = visibleEmojisRef.current.filter(
          (e) => !collectedRef.current.includes(e.id)
        );
        if (available.length) {
          const treasure = available[random(0, available.length - 1)];
          setTreasureEmojiId(treasure.id);
        }
      }

      if (queueRef.current.length < 15) {
        loadEmojiBatch();
      }
    },
    [
      playing,
      settings.category,
      goldenEmojiId,
      treasureEmojiId,
      removeVisibleEmoji,
      pushToGrid,
      loadEmojiBatch,
    ]
  );

  // ─── Game Control ────────────────────────────────────────────────────────
  const stopGame = useCallback(() => {
    setPlaying(false);
    setFinished(true);
    clearInterval(timerRef.current);
    clearInterval(scrollRef.current);
  }, []);

  const finishGame = useCallback(async () => {
    stopGame();
    setGameMessage("🎉 Game Complete! Well Done!");
    playWin();

    const total = correct + wrong;
    const acc = total === 0 ? 100 : Math.round((correct / total) * 100);
    setAccuracy(acc);

    let finalScore = score;

    // Flawless / Lightning bonus
    const FLAWLESS_BONUS = {
      3: { seconds: 15, points: 100 },
      4: { seconds: 30, points: 200 },
      5: { seconds: 45, points: 300 },
    };

    const hasPenalty = scoreLedger.some((e) => e.points < 0);
    const bonusRule = FLAWLESS_BONUS[settings.gridSize];

    if (!hasPenalty && bonusRule && elapsed <= bonusRule.seconds * 1000) {
      const isLightning = elapsed <= (bonusRule.seconds * 1000) / 2;
      const bonusPoints = isLightning ? bonusRule.points * 2 : bonusRule.points;

      finalScore += bonusPoints;
      setScore(finalScore);
      setScoreLedger((prev) => [
        ...prev,
        {
          type: "bonus",
          points: bonusPoints,
          reason: isLightning
            ? `⚡ Lightning Clear — ${settings.gridSize}×${settings.gridSize} in under ${bonusRule.seconds / 2}s!`
            : `✨ Flawless Clear — ${settings.gridSize}×${settings.gridSize} under ${bonusRule.seconds}s!`,
          at: elapsed,
        },
      ]);
    }

    const xp = finalScore + acc * 10 + bestCombo * 25;

    try {
      await submitScore({
        score: finalScore,
        gridSize: settings.gridSize,
        category: settings.category,
        speed: settings.speed,
        accuracy: acc,
        collected: correct,
        total: gridCapacity,
        combo: bestCombo,
        timeTaken: elapsed,
        xp,
      });

      await confirmStreakEntry({
        score: finalScore,
        gridSize: settings.gridSize,
        category: settings.category,
        speed: settings.speed,
        accuracy: acc,
        timeTaken: elapsed,
        xp,
        userCode,
        userName,
      });

      // Refresh data so home / history / leaderboard stay up to date
      fetchHistory();
    } catch (err) {
      console.error(err);
    }
  }, [
    stopGame,
    correct,
    wrong,
    score,
    scoreLedger,
    bestCombo,
    elapsed,
    settings,
    gridCapacity,
    submitScore,
    confirmStreakEntry,
    fetchHistory,
    userCode,
    userName,
  ]);

  const startGame = useCallback(async () => {
    setLoading(true);

    // Full reset
    setScore(0);
    setCombo(0);
    comboRef.current = 0;
    setBestCombo(0);
    setCorrect(0);
    setWrong(0);
    setAccuracy(100);
    setElapsed(0);
    setProgress(0);
    setGameMessage("");
    setGoldenEmojiId(null);
    setTreasureEmojiId(null);
    setTargetTime(30 + Math.floor(Math.random() * 16));
    setFreezeUntil(0);
    setFrozen(false);
    setScoreLedger([]);

    collectedRef.current = [];
    queueRef.current = [];
    setCollectedIds([]);
    setGrid([]);
    setVisibleEmojis([]);
    setEmojiQueue([]);

    await loadEmojiBatch();

    // Small delay so first batch is ready, then fill the visible strip
    setTimeout(() => {
      refillVisible();
    }, 100);

    setFinished(false);
    setPlaying(false); // stay paused until user presses Start
    setScreen("play");
    setLoading(false);
  }, [loadEmojiBatch, refillVisible]);

  const beginPlaying = useCallback(() => {
    if (playing || finished) return;
    setPlaying(true);
  }, [playing, finished]);

  const restartGame = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(scrollRef.current);
    startGame();
  }, [startGame]);

  const backHome = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(scrollRef.current);

    setScreen("home");
    setPlaying(false);
    setFinished(false);
    setGrid([]);
    setVisibleEmojis([]);
    setEmojiQueue([]);
    queueRef.current = [];
    collectedRef.current = [];
    setCollectedIds([]);
    setProgress(0);
    setScore(0);
    setCombo(0);
    setCorrect(0);
    setWrong(0);
    setElapsed(0);
    setGameMessage("");
    setFrozen(false);
    setFreezeUntil(0);
  }, []);

  const resetGame = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(scrollRef.current);

    queueRef.current = [];
    collectedRef.current = [];
    setEmojiQueue([]);
    setVisibleEmojis([]);
    setCollectedIds([]);
    setGrid([]);
    setScore(0);
    setCombo(0);
    comboRef.current = 0;
    setBestCombo(0);
    setCorrect(0);
    setWrong(0);
    setAccuracy(100);
    setElapsed(0);
    setProgress(0);
    setGoldenEmojiId(null);
    setTreasureEmojiId(null);
    setFreezeUntil(0);
    setFrozen(false);
    setGameMessage("");
    setPlaying(false);
    setFinished(false);
    setScreen("home");
    setTargetTime(30 + Math.floor(Math.random() * 16));
    setScoreLedger([]);
  }, []);

  const freezeGame = useCallback(
    (seconds = 5) => {
      if (!playing) return;

      setFrozen(true);
      setFreezeUntil(Date.now() + seconds * 1000);
      setScore((prev) => prev - 10);
      setScoreLedger((prev) => [
        ...prev,
        {
          type: "freeze",
          points: -PENALTY,
          reason: "Freeze",
          at: elapsedRef.current,
        },
      ]);
      setGameMessage("⏸ Frozen");
      playFreeze?.();
    },
    [playing]
  );

  const activateGoldenEmoji = useCallback(() => {
    if (!visibleEmojis.length) return;
    const lucky = visibleEmojis[random(0, visibleEmojis.length - 1)];
    setGoldenEmojiId(lucky.id);
    setGameMessage("🌟 Golden Emoji!");
  }, [visibleEmojis]);

  const activateTreasureEmoji = useCallback(() => {
    if (!visibleEmojis.length) return;
    const lucky = visibleEmojis[random(0, visibleEmojis.length - 1)];
    setTreasureEmojiId(lucky.id);
    setGameMessage("💎 Treasure!");
  }, [visibleEmojis]);

  const clearBonuses = useCallback(() => {
    setGoldenEmojiId(null);
    setTreasureEmojiId(null);
  }, []);

  // ─── Effects ─────────────────────────────────────────────────────────────

  // Timer
  useEffect(() => {
    if (!playing) return;

    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsed((prev) => prev + 100);
    }, 100);

    return () => clearInterval(timerRef.current);
  }, [playing]);

// Auto-scroll / emoji conveyor
    useEffect(() => {
    if (!playing) return;

    clearInterval(scrollRef.current);

    scrollRef.current = setInterval(() => {
        if (frozen) return;

        // 1. Read the current list once (outside any setState)
        const current = visibleEmojisRef.current;
        if (current.length === 0) return;

        const missedEmoji = current[0];

        // 2. Apply miss penalty (side-effects are safe here)
        const isValidTarget =
        settings.category === "mixed" ||
        missedEmoji.category === settings.category;

        if (
        isValidTarget &&
        !collectedRef.current.includes(missedEmoji.id)
        ) {
        const label =
            missedEmoji.char ||
            missedEmoji.emoji ||
            missedEmoji.symbol ||
            missedEmoji.name ||
            "Emoji";

        setScore((s) => s - PENALTY);
        setWrong((w) => w + 1);
        comboRef.current = 0;
        setCombo(0);
        setGameMessage("❌ Missed!");
        playWrong();

        setScoreLedger((ledger) => [
            ...ledger,
            {
            type: "miss",
            points: -PENALTY,
            reason: `${label} (missed)`,
            at: elapsedRef.current,
            },
        ]);
        }

        // 3. Now update the visible list (pure)
        setVisibleEmojis((prev) => {
        if (prev.length === 0) return prev;

        const list = prev.slice(1); // remove the first one

        if (queueRef.current.length) {
            list.push(queueRef.current.shift());
        }

        if (queueRef.current.length < 15) {
            loadEmojiBatch();
        }

        return list;
        });
    }, SPEEDS[settings.speed] || SPEEDS.medium);

    return () => clearInterval(scrollRef.current);
  }, [playing, settings.speed, settings.category, frozen, loadEmojiBatch]);

  // Win condition
  useEffect(() => {
    if (grid.length >= gridCapacity && playing) {
      finishGame();
    }
  }, [grid.length, gridCapacity, playing, finishGame]);

  // Keep accuracy in sync
  useEffect(() => {
    if (!playing) return;
    const total = correct + wrong;
    if (total === 0) return;
    setAccuracy(Math.round((correct / total) * 100));
  }, [correct, wrong, playing]);

  // Auto-unfreeze
  useEffect(() => {
    if (!frozen || freezeUntil <= 0) return;

    const remaining = freezeUntil - Date.now();
    if (remaining <= 0) {
      setFrozen(false);
      setFreezeUntil(0);
      return;
    }

    const id = setTimeout(() => {
      setFrozen(false);
      setFreezeUntil(0);
      setGameMessage("");
    }, remaining);

    return () => clearTimeout(id);
  }, [frozen, freezeUntil]);

  // ─── Public API ──────────────────────────────────────────────────────────
  return {
    // User
    user: userId,

    // Screen / Loading
    loading,
    screen,
    setScreen,
    playing,
    finished,

    // Settings
    settings,
    gridSize: settings.gridSize,
    category: settings.category,
    speed: settings.speed,
    setGridSize: changeGridSize,
    setSpeed: changeSpeed,
    setCategory,
    updateSettings,

    // Game board
    emojiQueue,
    visibleEmojis,
    grid,
    collectedIds,
    gridCapacity,
    progress,

    // Score & stats
    score,
    scoreLedger,
    combo,
    bestCombo,
    correct,
    wrong,
    accuracy,
    elapsed,
    targetTime,
    gameMessage,

    // Specials
    goldenEmojiId,
    treasureEmojiId,
    freezeUntil,
    frozen,

    // Backend data
    leaderboard,
    streakHistory,
    bestRecords,
    myStats,
    currentStreak: streakHistory[0]?.streakDays || 0,
    loadHistory: fetchHistory,
    loadLeaderboard: fetchLeaderboard,
    fetchHistory,
    fetchLeaderboard,

    // Actions
    startGame,
    beginPlaying,
    stopGame,
    restartGame,
    backHome,
    resetGame,
    finishGame,
    onEmojiTap,
    freezeGame,
    activateGoldenEmoji,
    activateTreasureEmoji,
    clearBonuses,
    loadEmojiBatch,
  };
}